/*
 * Contrast Bundle pages: shared behavior for the bundle-* sections.
 *
 * - Helpers: money formatting and scrolling to an in-page anchor.
 * - <bl-buy-box>: gallery, quantity (tier pills or a stepper), prices and
 *   Buy now (adds to the cart and opens the cart drawer). Tiers come from the "Bundle · Quantity tiers"
 *   section's <script data-bl-tiers> JSON, so they're set up in one place.
 *   Every change is announced as a `bl:quantity` event on document, which
 *   the tiers table and the sticky bar listen to.
 *   BundleLP.setQuantity(qty) lets other sections pick a quantity.
 * - Theme editor: sections register handlers for block select/deselect.
 *
 * Each section that needs it includes this file, so it guards against
 * running twice.
 */
(() => {
  if (window.BundleLP) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Helpers ---------------------------------------------------------------- */

  // cents: integer. format: the shop's money format, e.g. "${{amount}}".
  function formatMoney(cents, format) {
    const template = format || '${{amount_no_decimals}}';
    const value = Math.round(Number(cents) || 0);
    const withDelimiters = (number, decimals, thousands, decimal) => {
      const [whole, fraction] = (number / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount':
          return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  // Like Liquid's money_without_trailing_zeros: $5,600.00 → $5,600.
  function formatPrice(cents, format) {
    return formatMoney(cents, format).replace(/[.,]00(?=\D*$)/, '');
  }

  // target: "#buy", "buy" or an element. The theme's scroll-padding-top
  // (or the kiosk bar's) keeps the target clear of sticky headers.
  function scrollToTarget(target) {
    const el =
      typeof target === 'string' ? document.getElementById(target.replace(/^#/, '')) : target;
    if (!el) return false;
    el.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    return true;
  }

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  /* Theme editor ------------------------------------------------------------ */

  // handlers[event] = [fn(event)]; the section's own code checks whether the
  // event's block belongs to it.
  const handlers = { select: [], deselect: [] };

  function onEditorBlock(kind, fn) {
    handlers[kind].push(fn);
  }

  document.addEventListener('shopify:block:select', (event) => {
    handlers.select.forEach((fn) => fn(event));
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    handlers.deselect.forEach((fn) => fn(event));
  });

  /* Tiers -------------------------------------------------------------------- */

  // [{ quantity, label, savings, percent, code }], sorted by quantity, one per
  // quantity. Empty when the page has no tiers section.
  function readTiers() {
    const data = readJson(document.querySelector('script[data-bl-tiers]'));
    const seen = new Set();
    return (Array.isArray(data) ? data : [])
      .map((tier) => ({
        quantity: Math.max(1, parseInt(tier.quantity, 10) || 1),
        label: String(tier.label || ''),
        savings: String(tier.savings || ''),
        percent: Math.min(100, Math.max(0, Number(tier.percent) || 0)),
        code: String(tier.code || '').trim(),
      }))
      .sort((a, b) => a.quantity - b.quantity)
      .filter((tier) => (seen.has(tier.quantity) ? false : seen.add(tier.quantity)));
  }

  /* <bl-buy-box> ------------------------------------------------------------- */

  const buyBoxes = new Set();

  // The latest bl:quantity detail, for elements that connect after it fired.
  let lastDetail = null;

  class BlBuyBox extends HTMLElement {
    connectedCallback() {
      this.data = readJson(this.querySelector('script[data-bl-product]')) || {};
      this.qty = 1;
      this.el = {
        pills: this.querySelector('[data-bl-pills]'),
        stepper: this.querySelector('[data-bl-stepper]'),
        qty: this.querySelector('[data-bl-qty]'),
        price: this.querySelector('[data-bl-price]'),
        savings: this.querySelector('[data-bl-savings]'),
        total: this.querySelector('[data-bl-total]'),
        buy: this.querySelector('[data-bl-buy]'),
        addon: this.querySelector('[data-bl-addon]'),
        noTiersNote: this.querySelector('[data-bl-no-tiers-note]'),
      };

      // Once per element: the editor re-connects elements when sections move.
      if (!this.listening) {
        this.listening = true;
        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-bl-thumb]');
          if (thumb) this.showSlide(Number(thumb.dataset.blThumb));
          const step = event.target.closest('[data-bl-step]');
          if (step) this.setQuantity(this.qty + Number(step.dataset.blStep));
          const pill = event.target.closest('[data-bl-pill]');
          if (pill) this.setQuantity(Number(pill.dataset.blPill));
          const buy = event.target.closest('[data-bl-buy]');
          if (buy && this.cartItems) {
            event.preventDefault();
            this.addToCart();
          }
          const arrow = event.target.closest('[data-bl-carousel-step]');
          if (arrow) stepCarousel(arrow.closest('[data-bl-carousel]'), Number(arrow.dataset.blCarouselStep));
        });
        // Carousel counter and arrows follow swipes too. Scroll doesn't bubble,
        // so listen in the capture phase.
        this.addEventListener(
          'scroll',
          (event) => {
            const carousel = event.target.closest && event.target.closest('[data-bl-carousel]');
            if (carousel) updateCarousel(carousel);
          },
          { capture: true, passive: true }
        );
        // The add-on checkbox (e.g. white glove installation).
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-bl-addon]')) this.render();
        });
      }

      buyBoxes.add(this);
      this.querySelectorAll('[data-bl-carousel]').forEach(updateCarousel);
      this.refreshTiers();
    }

    disconnectedCallback() {
      buyBoxes.delete(this);
    }

    // Re-read the tiers section (on load, and when the editor reloads it).
    refreshTiers() {
      this.tiers = this.data.mode === 'tiers' ? readTiers() : [];
      this.mode = this.tiers.length ? 'tiers' : 'stepper';
      const tiersMode = this.mode === 'tiers';

      this.el.pills.hidden = !tiersMode;
      this.el.stepper.hidden = tiersMode;
      this.querySelectorAll('[data-bl-tiers-only]').forEach((el) => {
        el.hidden = !tiersMode;
      });
      if (this.el.noTiersNote) this.el.noTiersNote.hidden = this.data.mode !== 'tiers' || tiersMode;

      if (tiersMode) this.renderPills();
      this.setQuantity(this.qty, { force: true });
    }

    renderPills() {
      const template = this.data.pillTemplate || '[label]';
      this.el.pills.replaceChildren(
        ...this.tiers.map((tier) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'bl-pill';
          pill.dataset.blPill = tier.quantity;
          pill.textContent =
            tier.percent > 0
              ? template.replace('[label]', tier.label).replace('[percent]', String(tier.percent))
              : tier.label;
          return pill;
        })
      );
    }

    tierFor(qty) {
      return this.tiers.find((tier) => tier.quantity === qty);
    }

    setQuantity(requested, { force = false } = {}) {
      let qty = Math.round(Number(requested) || 1);
      if (this.mode === 'tiers') {
        // Not a tier: keep the current choice if it's still a tier.
        if (!this.tierFor(qty)) qty = this.tierFor(this.qty) ? this.qty : this.tiers[0].quantity;
      } else {
        qty = Math.min(Math.max(qty, 1), Math.max(1, Number(this.data.stepperMax) || 1));
      }
      if (qty === this.qty && !force) return;
      this.qty = qty;
      this.render();
    }

    render() {
      const { data, qty } = this;
      const tier = this.mode === 'tiers' ? this.tierFor(qty) : null;
      const hasPrice = typeof data.price === 'number';
      const unit = hasPrice ? Math.round((data.price * (100 - (tier ? tier.percent : 0))) / 100) : null;
      // Add-on: one per bundle, at its own price.
      const addonOn = Boolean(this.el.addon && this.el.addon.checked && data.addonVariantId);
      const addonTotal = addonOn ? (Number(data.addonPrice) || 0) * qty : 0;
      const total = hasPrice ? unit * qty + addonTotal : null;
      const money = (cents) => formatPrice(cents, data.moneyFormat);

      // Quantity controls.
      this.el.pills.querySelectorAll('[data-bl-pill]').forEach((pill) => {
        const selected = Number(pill.dataset.blPill) === qty;
        pill.classList.toggle('is-selected', selected);
        pill.setAttribute('aria-pressed', String(selected));
      });
      this.el.qty.textContent = String(qty);
      this.el.stepper.querySelector('[data-bl-step="-1"]').disabled = qty <= 1;
      this.el.stepper.querySelector('[data-bl-step="1"]').disabled = qty >= (Number(data.stepperMax) || 1);

      // Prices.
      if (hasPrice) {
        this.el.price.textContent = money(unit);
        if (this.el.savings) {
          const saving = data.compare - unit;
          this.el.savings.textContent = money(saving);
          this.el.savings.parentElement.hidden = saving <= 0;
        }
        // With the add-on the total always shows; without it, from 2 bundles.
        const totalTemplate = addonOn ? data.addonTotalTemplate || data.totalTemplate : data.totalTemplate;
        this.el.total.hidden = !addonOn && qty <= 1;
        this.el.total.textContent = (totalTemplate || '')
          .replace('[qty]', String(qty))
          .replace('[total]', money(total));
      }

      // What Buy now adds to the cart: the bundle, plus one add-on per bundle
      // when it's ticked, and the tier's (or stepper's) discount code.
      this.code = tier ? tier.code : String(data.stepperCode || '').trim();
      this.cartItems = null;
      if (data.variantId && data.available) {
        this.cartItems = [{ id: data.variantId, quantity: qty }];
        if (addonOn) this.cartItems.push({ id: data.addonVariantId, quantity: qty });
      }

      // Per-bundle price of every tier, for the tiers table.
      const tierPrices = {};
      if (hasPrice) {
        this.tiers.forEach((t) => {
          tierPrices[t.quantity] = money(Math.round((data.price * (100 - t.percent)) / 100));
        });
      }

      lastDetail = {
        source: this,
        qty,
        mode: this.mode,
        unit,
        total,
        unitText: hasPrice ? money(unit) : '',
        totalText: hasPrice ? money(total) : '',
        tierPrices,
        addon: addonOn,
        canBuy: Boolean(this.cartItems),
        available: Boolean(data.available),
      };
      document.dispatchEvent(new CustomEvent('bl:quantity', { detail: lastDetail }));
    }

    // Adds the bundle (and add-on) with /cart/add.js, then opens the cart
    // drawer. Cart links (/cart/123:1) can't add bundle products, so they're
    // not used. A discount code is applied first with /discount/CODE, which
    // stores it for checkout.
    async addToCart() {
      if (!this.cartItems || this.busy) return false;
      this.busy = true;
      const buttons = document.querySelectorAll('[data-bl-buy], [data-bl-sticky-buy]');
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      this.showError('');

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        if (this.code) {
          await fetch(`${root}discount/${encodeURIComponent(this.code)}`, { credentials: 'same-origin' });
        }
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.cartItems }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Could not add to cart.');
        }
        await openCartDrawer(this.data.cartUrl);
        return true;
      } catch (error) {
        this.showError(error.message);
        scrollToTarget(this.el.buy || this);
        return false;
      } finally {
        this.busy = false;
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
      }
    }

    showError(message) {
      const box = this.querySelector('[data-bl-buy-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
    }

    showSlide(index) {
      this.querySelectorAll('[data-bl-slide]').forEach((slide) => {
        slide.hidden = Number(slide.dataset.blSlide) !== index;
        // A carousel that was hidden had no width: refresh its counter now.
        if (!slide.hidden) slide.querySelectorAll('[data-bl-carousel]').forEach(updateCarousel);
      });
      this.querySelectorAll('[data-bl-thumb]').forEach((thumb) => {
        thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.blThumb) === index));
      });
    }
  }

  customElements.define('bl-buy-box', BlBuyBox);

  /* Gallery carousels (a slide that shows a product's images) --------------- */

  // The track scrolls sideways with scroll snapping: one image per view.
  function carouselIndex(track) {
    return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  }

  function stepCarousel(carousel, step) {
    const track = carousel && carousel.querySelector('[data-bl-carousel-track]');
    if (!track) return;
    const last = track.children.length - 1;
    const next = Math.min(Math.max(carouselIndex(track) + step, 0), last);
    track.scrollTo({ left: next * track.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  function updateCarousel(carousel) {
    const track = carousel.querySelector('[data-bl-carousel-track]');
    if (!track) return;
    const index = carouselIndex(track);
    const total = track.children.length;
    const count = carousel.querySelector('[data-bl-carousel-count]');
    if (count) count.textContent = `${index + 1} / ${total}`;
    const prev = carousel.querySelector('[data-bl-carousel-step="-1"]');
    const next = carousel.querySelector('[data-bl-carousel-step="1"]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= total - 1;
  }

  // Opens the store's cart drawer after an add: the Essential Cart app's
  // drawer when it's installed, else the theme's own cart drawer, else the
  // cart page.
  async function openCartDrawer(cartUrl) {
    const essential = window.essentialCartApi;
    if (essential && typeof essential.openCart === 'function') {
      if (typeof essential.refreshCart === 'function') await essential.refreshCart();
      essential.openCart();
      return;
    }
    const themeDrawer = document.getElementById('cart-modal');
    const trigger = document.querySelector('[target="#cart-modal"], [data-target="#cart-modal"]');
    if (themeDrawer && trigger) {
      if (typeof themeDrawer.reloadContent === 'function') await themeDrawer.reloadContent();
      trigger.click();
      return;
    }
    window.location.assign(cartUrl || '/cart');
  }

  function setQuantity(qty) {
    buyBoxes.forEach((box) => box.setQuantity(qty));
  }

  // The latest bl:quantity detail, or null when the page has no buy box.
  function currentState() {
    return lastDetail && buyBoxes.has(lastDetail.source) ? lastDetail : null;
  }

  // Calls fn(detail) now (if a buy box has rendered) and on every change.
  // Returns a function that stops listening.
  function onQuantity(fn) {
    const state = currentState();
    if (state) fn(state);
    const listener = (event) => fn(event.detail);
    document.addEventListener('bl:quantity', listener);
    return () => document.removeEventListener('bl:quantity', listener);
  }

  // The buy box's section (it holds the #buy anchor), looked up fresh because
  // the editor replaces sections when they're edited.
  function buyBoxSection() {
    const [box] = buyBoxes;
    return box ? box.closest('.section') || box : null;
  }

  function scrollToBuyBox() {
    const target = buyBoxSection();
    if (target) scrollToTarget(target);
  }

  // The editor reloads one section at a time: when the tiers section changes,
  // the buy box re-reads it. Unload fires before the old markup is removed.
  ['shopify:section:load', 'shopify:section:unload'].forEach((name) => {
    document.addEventListener(name, () => {
      setTimeout(() => buyBoxes.forEach((box) => box.refreshTiers()));
    });
  });

  // Open the FAQ question selected in the editor.
  onEditorBlock('select', (event) => {
    if (event.target.matches && event.target.matches('[data-bl-faq]')) event.target.open = true;
  });

  onEditorBlock('select', (event) => {
    const slide = event.target.closest && event.target.closest('[data-bl-slide]');
    const box = slide && slide.closest('bl-buy-box');
    if (box) box.showSlide(Number(slide.dataset.blSlide));
  });

  /* <bl-calculator> ---------------------------------------------------------- */

  // Monthly revenue = sessions per day × people per session (optional) × price
  // per person × days per month.
  // Shows the empty text until a price above 0 is entered.
  class BlCalculator extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-bl-calc-output]');
      if (!this.listening) {
        this.listening = true;
        this.addEventListener('input', () => this.update());
      }
      this.update();
    }

    value(name) {
      const input = this.querySelector(`[data-bl-calc="${name}"]`);
      const number = parseFloat(input ? input.value : '');
      return Number.isFinite(number) && number > 0 ? number : 0;
    }

    update() {
      const price = this.value('price');
      // Optional "people per session" field; without it, one person per session.
      const people = this.querySelector('[data-bl-calc="people"]') ? this.value('people') : 1;
      this.output.textContent =
        price > 0
          ? formatPrice(
              Math.round(this.value('sessions') * people * price * this.value('days') * 100),
              this.dataset.moneyFormat
            )
          : this.dataset.empty || '';
    }
  }

  customElements.define('bl-calculator', BlCalculator);

  /* <bl-tiers> -------------------------------------------------------------- */

  // The quantity tiers table: fills each row's per-bundle price from the buy
  // box, marks the chosen row, and picks a quantity on click. Hidden on the
  // live page when the buy box is in Stepper mode.
  class BlTiers extends HTMLElement {
    connectedCallback() {
      this.designMode = this.dataset.designMode === 'true';
      if (!this.listening) {
        this.listening = true;
        this.addEventListener('click', (event) => {
          const row = event.target.closest('[data-bl-tier-row]');
          if (!row) return;
          setQuantity(Number(row.dataset.blTierRow));
          scrollToBuyBox();
        });
      }
      this.unsubscribe = onQuantity((detail) => this.update(detail));
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    update(detail) {
      const stepper = detail.mode === 'stepper';
      const section = this.closest('.shopify-section');
      if (section && !this.designMode) section.hidden = stepper;
      const note = this.querySelector('[data-bl-stepper-note]');
      if (note) note.hidden = !stepper;

      this.querySelectorAll('[data-bl-tier-price]').forEach((cell) => {
        cell.textContent = detail.tierPrices[cell.dataset.blTierPrice] || '—';
      });
      this.querySelectorAll('[data-bl-tier-row]').forEach((row) => {
        row.setAttribute('aria-pressed', String(!stepper && Number(row.dataset.blTierRow) === detail.qty));
      });
    }
  }

  customElements.define('bl-tiers', BlTiers);

  /* <bl-sticky-bar> ---------------------------------------------------------- */

  // Shown once the buy box has scrolled out of view above; mirrors its
  // quantity and total, and its button adds to the cart the same way. In the editor, selecting the section
  // shows it too.
  class BlStickyBar extends HTMLElement {
    connectedCallback() {
      this.el = {
        title: this.querySelector('[data-bl-sticky-title]'),
        qty: this.querySelector('[data-bl-sticky-qty]'),
        total: this.querySelector('[data-bl-sticky-total]'),
        buy: this.querySelector('[data-bl-sticky-buy]'),
      };
      this.pastBuyBox = false;
      this.editorSelected = false;

      if (!this.listening) {
        this.listening = true;
        // Same add-to-cart as the buy box; without a buyable bundle, scroll there.
        this.el.buy.addEventListener('click', (event) => {
          event.preventDefault();
          const box = this.source && this.source.isConnected ? this.source : null;
          if (box && this.canBuy) box.addToCart();
          else scrollToBuyBox();
        });
      }

      // Past the buy box = its section's bottom edge is above the viewport.
      // The section is looked up on every check: the editor replaces it.
      this.onScroll = () => {
        const target = buyBoxSection();
        const past = Boolean(target && target.isConnected && target.getBoundingClientRect().bottom < 0);
        if (past !== this.pastBuyBox) {
          this.pastBuyBox = past;
          this.render();
        }
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.unsubscribe = onQuantity((detail) => this.update(detail));
      this.onScroll();

      this.onSelect = (event) => this.toggleEditor(event, true);
      this.onDeselect = (event) => this.toggleEditor(event, false);
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
    }

    toggleEditor(event, selected) {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) {
        this.editorSelected = selected;
        this.render();
      }
    }

    update(detail) {
      // Without its own title, use the buy box's.
      if (!this.el.title.textContent.trim() && detail.source) {
        const title = detail.source.querySelector('.bl-buy__title');
        if (title) this.el.title.textContent = title.textContent.trim();
      }
      this.source = detail.source;
      this.canBuy = detail.canBuy;
      this.el.qty.textContent = (this.dataset.qtyTemplate || '').replace('[qty]', String(detail.qty));
      this.el.total.textContent = detail.totalText;
    }

    render() {
      const visible = this.pastBuyBox || this.editorSelected;
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
    }
  }

  customElements.define('bl-sticky-bar', BlStickyBar);

  /* <bl-kiosk> ---------------------------------------------------------------- */

  // Kiosk mode for the event iPad. Activation "url": ?kiosk=1 turns it on for
  // this browser tab (sessionStorage, so it carries over to other bundle
  // pages) and remembers that address as the start page; ?kiosk=0 turns it
  // off. While on: <html> gets .bl-kiosk (and .bl-kiosk-bare to hide the
  // store header and footer), "Start over" buttons ([data-bl-reset]) go back
  // to the start page, and after the idle time a countdown does the same.
  const KIOSK_KEY = 'bl-kiosk';
  const KIOSK_START_KEY = 'bl-kiosk-start';

  function session(method, key, value) {
    try {
      return window.sessionStorage[method](key, value);
    } catch (error) {
      return null;
    }
  }

  class BlKiosk extends HTMLElement {
    connectedCallback() {
      if (this.dataset.designMode === 'true' || this.started) return;
      const activation = this.dataset.activation;
      const params = new URLSearchParams(window.location.search);

      if (params.get('kiosk') === '0') {
        session('removeItem', KIOSK_KEY);
        session('removeItem', KIOSK_START_KEY);
      } else if (params.get('kiosk') === '1' && activation === 'url') {
        session('setItem', KIOSK_KEY, '1');
        session('setItem', KIOSK_START_KEY, window.location.pathname + window.location.search);
      }

      const on = activation === 'always' || (activation === 'url' && session('getItem', KIOSK_KEY) === '1');
      if (!on) return;
      this.started = true;

      this.startUrl = session('getItem', KIOSK_START_KEY) || window.location.pathname;
      this.idleMs = (Number(this.dataset.idleSeconds) || 120) * 1000;
      this.countdownFrom = Number(this.dataset.countdownSeconds) || 10;
      this.dialog = this.querySelector('[data-bl-kiosk-dialog]');
      this.count = this.querySelector('[data-bl-kiosk-count]');

      const root = document.documentElement;
      root.classList.add('bl-kiosk');
      if (this.dataset.hideChrome === 'true') root.classList.add('bl-kiosk-bare');

      // Each reset should start at the top, not where the last guest was.
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

      this.lastActivity = Date.now();
      this.onActivity = () => {
        if (!this.dialog.open) this.lastActivity = Date.now();
      };
      // Escape closes the modal dialog: count that as "I'm still here".
      this.dialog.addEventListener('close', () => {
        this.lastActivity = Date.now();
      });
      ['pointerdown', 'keydown', 'scroll', 'touchstart', 'input'].forEach((name) => {
        window.addEventListener(name, this.onActivity, { passive: true, capture: true });
      });

      this.onClick = (event) => {
        if (event.target.closest('[data-bl-reset]')) this.reset();
        if (event.target.closest('[data-bl-kiosk-stay]')) this.stay();
      };
      document.addEventListener('click', this.onClick);

      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (!this.started) return;
      window.clearInterval(this.timer);
      ['pointerdown', 'keydown', 'scroll', 'touchstart', 'input'].forEach((name) => {
        window.removeEventListener(name, this.onActivity, { capture: true });
      });
      document.removeEventListener('click', this.onClick);
      this.started = false;
    }

    tick() {
      if (!this.dialog.open) {
        if (Date.now() - this.lastActivity >= this.idleMs) {
          this.remaining = this.countdownFrom;
          this.showCountdown();
        }
        return;
      }
      this.remaining -= 1;
      if (this.remaining <= 0) this.reset();
      else if (this.count) this.count.textContent = String(this.remaining);
    }

    // A modal <dialog>: the page behind is inert and focus stays inside;
    // closing it puts focus back where it was.
    showCountdown() {
      if (this.count) this.count.textContent = String(this.remaining);
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      const stay = this.dialog.querySelector('[data-bl-kiosk-stay]');
      if (stay) stay.focus();
    }

    stay() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      this.lastActivity = Date.now();
    }

    // Back to the start page: clears the quantity, the calculator and the
    // email form, even when the guest wandered to another bundle page.
    reset() {
      window.clearInterval(this.timer);
      window.scrollTo(0, 0);
      window.location.assign(this.startUrl);
    }
  }

  customElements.define('bl-kiosk', BlKiosk);

  /* Email capture (Shopify customer form) ----------------------------------- */

  // Shopify's customer form only stores a few fields, so the practice name
  // goes into the tags: "Disrupt event, Practice: Desert Wellness".
  function setSignupTags(form) {
    const tags = form.querySelector('[data-bl-tags]');
    const practice = form.querySelector('[data-bl-practice]');
    if (!tags) return;
    const name = practice ? practice.value.replace(/[,\s]+/g, ' ').trim() : '';
    tags.value = [tags.dataset.blTags, name ? `Practice: ${name}` : ''].filter(Boolean).join(', ');
  }

  // Sends the form in the background (no page reload). Shopify answers with
  // the page re-rendered for the result, so the form's new contents (success
  // message or error) are taken from it. If Shopify asks for its spam check
  // (/challenge) or anything fails, the form is submitted the normal way.
  async function submitSignup(form) {
    const button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      });
      if (!response.ok || /\/challenge/.test(response.url)) throw new Error('fallback');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const updated = page.getElementById(form.id);
      if (!updated) throw new Error('fallback');
      form.replaceChildren(...Array.from(updated.childNodes).map((node) => document.importNode(node, true)));
      const result = form.querySelector('[data-bl-signup-success], .bl-signup__error');
      if (result) {
        if (result.matches('[data-bl-signup-success]')) result.focus({ preventScroll: true });
        scrollToTarget(form.closest('.section') || form);
      }
    } catch (error) {
      HTMLFormElement.prototype.submit.call(form);
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest && event.target.closest('[data-bl-signup]');
    if (!form) return;
    setSignupTags(form);
    if (!window.fetch || !window.DOMParser || !form.id) return; // normal submit
    event.preventDefault();
    submitSignup(form);
  });

  // After the page reloads with the result, bring the message into view.
  document.addEventListener('DOMContentLoaded', () => {
    const result = document.querySelector('[data-bl-signup-success], .bl-signup__error');
    if (!result) return;
    scrollToTarget(result.closest('.section') || result);
    if (result.matches('[data-bl-signup-success]')) result.focus({ preventScroll: true });
  });

  window.BundleLP = {
    version: 1,
    formatMoney,
    formatPrice,
    scrollToTarget,
    onEditorBlock,
    readTiers,
    setQuantity,
    currentState,
    onQuantity,
    scrollToBuyBox,
  };
})();
